import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

/**
 * OAuth2 Callback Page
 * 
 * This page handles the redirect from Okta after authentication.
 * The react-oidc-context library automatically processes the authorization code
 * and exchanges it for tokens.
 * 
 * Once authentication is complete, this component redirects to the home page.
 */
export function CallbackPage() {
  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    console.log('[CallbackPage] Mounted');
    console.log('[CallbackPage] Auth state:', {
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      hasError: !!auth.error,
      error: auth.error?.message
    });

    // If authentication is complete, redirect to home
    if (!auth.isLoading && auth.isAuthenticated) {
      console.log('[CallbackPage] ✅ Authentication successful, redirecting to home');
      
      // Check if there's a return URL stored before login
      const returnUrl = sessionStorage.getItem('approvn_return_url');
      sessionStorage.removeItem('approvn_return_url');
      
      const destination = returnUrl || '/dashboard';
      console.log('[CallbackPage] Redirecting to:', destination);
      navigate(destination, { replace: true });
    }

    // If there's an error, show it
    if (auth.error) {
      console.error('[CallbackPage] ❌ Authentication error:', auth.error);
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate]);

  // Show loading state while processing the callback
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {auth.error ? (
          <>
            <div style={styles.errorIcon}>❌</div>
            <h1 style={styles.title}>Authentication Error</h1>
            <p style={styles.message}>{auth.error.message}</p>
            <button 
              style={styles.button}
              onClick={() => navigate('/')}
            >
              Return to Home
            </button>
          </>
        ) : (
          <>
            <div style={styles.spinner}></div>
            <h1 style={styles.title}>Completing Sign In...</h1>
            <p style={styles.message}>
              Please wait while we complete your authentication.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// Inline styles for the callback page
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '40px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    textAlign: 'center' as const,
    maxWidth: '400px',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold' as const,
    marginBottom: '10px',
    color: '#333',
  },
  message: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '20px',
  },
  button: {
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
